/**
 * The props each layout node type accepts.
 *
 * `types.ts` defines these, but interfaces vanish at runtime, so a layout
 * carrying a prop that does not exist -- `headerClassName` on a table, say --
 * parses, validates, renders, and silently does nothing. That failure is
 * invisible exactly when it matters most: a generated layout is authored by
 * something that has never seen the prop list, and the styling it thought it
 * applied is simply absent.
 *
 * Hand-maintained rather than derived, because the derivation would have to
 * run in the browser too. `knownNodeProps.test.ts` parses `types.ts` and fails
 * on any drift.
 */
export const KNOWN_NODE_PROPS: Readonly<Record<string, readonly string[]>> = {
  OASISDate: [
    'className',
    'controlClassName',
    'hideLabel',
    'innerClassName',
    'inputClassName',
    'labelClassName',
    'labelOverride',
    'qid',
    'style',
    'type',
  ],
  OASISMultiselect: [
    'checkboxClassName',
    'checklistClassName',
    'checklistStyle',
    'children',
    'className',
    'controlClassName',
    'hideLabel',
    'innerClassName',
    'labelClassName',
    'labelOverride',
    'optionLabelClassName',
    'optionRowClassName',
    'qid',
    'style',
    'type',
  ],
  OASISMultiselectOption: [
    'checkboxClassName',
    'className',
    'controlClassName',
    'labelClassName',
    'labelOverride',
    'optionRowClassName',
    'style',
    'type',
    'value',
    'valueFrom',
  ],
  OASISNumber: [
    'className',
    'controlClassName',
    'hideLabel',
    'innerClassName',
    'inputClassName',
    'labelClassName',
    'labelOverride',
    'qid',
    'style',
    'type',
  ],
  OASISRadio: [
    'className',
    'controlClassName',
    'groupClassName',
    'hideLabel',
    'inline',
    'innerClassName',
    'itemClassName',
    'labelClassName',
    'labelOverride',
    'optionLabelClassName',
    'optionRowClassName',
    'orientation',
    'qid',
    'style',
    'type',
  ],
  OASISPhysician: [
    'actionsClassName',
    'className',
    'controlClassName',
    'detailsClassName',
    'headerClassName',
    'hideLabel',
    'innerClassName',
    'inputClassName',
    'labelClassName',
    'labelOverride',
    'optionLabelClassName',
    'optionRowClassName',
    'qid',
    'style',
    'type',
  ],
  OASISSelect: [
    'className',
    'controlClassName',
    'hideLabel',
    'innerClassName',
    'labelClassName',
    'labelOverride',
    'placeholder',
    'qid',
    'style',
    'triggerClassName',
    'type',
  ],
  OASISText: [
    'className',
    'controlClassName',
    'hideLabel',
    'innerClassName',
    'inputClassName',
    'labelClassName',
    'labelOverride',
    'multiline',
    'qid',
    'style',
    'type',
  ],
  OASISTime: [
    'className',
    'controlClassName',
    'hideLabel',
    'innerClassName',
    'inputClassName',
    'labelClassName',
    'labelOverride',
    'qid',
    'style',
    'type',
  ],
  OASISYesNoCheckbox: [
    'checkboxClassName',
    'className',
    'controlClassName',
    'hideLabel',
    'innerClassName',
    'labelClassName',
    'labelOverride',
    'optionRowClassName',
    'qid',
    'style',
    'type',
  ],
  box: ['children', 'className', 'style', 'type'],
  card: ['children', 'className', 'style', 'type'],
  conditional: [
    'children',
    'className',
    'elseChildren',
    'style',
    'type',
    'when',
    'whenAll',
  ],
  'disabled-when': ['children', 'className', 'style', 'type', 'whenAll'],
  display: ['className', 'label', 'ref', 'style', 'type'],
  'external-answer': [
    'className',
    'empty',
    'hideWhenEmpty',
    'label',
    'labelClassName',
    'list',
    'ref',
    'source',
    'style',
    'type',
  ],
  divider: ['className', 'style', 'type'],
  'field-row': [
    'className',
    'controls',
    'gap',
    'label',
    'labelClassName',
    'rowClassName',
    'style',
    'type',
  ],
  flex: [
    'alignItems',
    'children',
    'className',
    'direction',
    'gap',
    'justifyContent',
    'style',
    'type',
    'variant',
    'wrap',
  ],
  grid: ['children', 'className', 'cols', 'gap', 'style', 'type', 'variant'],
  indent: ['children', 'className', 'style', 'type'],
  'radio-option': [
    'className',
    'controlClassName',
    'itemClassName',
    'qid',
    'style',
    'type',
    'value',
  ],
  repeat: ['as', 'children', 'className', 'over', 'style', 'type'],
  'section-label': ['className', 'style', 'text', 'type', 'variant'],
  stack: ['children', 'className', 'gap', 'style', 'type', 'variant'],
  static: ['as', 'className', 'style', 'text', 'type', 'variant'],
  sum: ['id', 'terms', 'type'],
  table: [
    'cellClassName',
    'cellStyle',
    'chrome',
    'className',
    'colHeaderClassName',
    'colHeaderStyle',
    'colHeaders',
    'rowClassName',
    'rowHeader',
    'rowHeaderClassName',
    'rowHeaderStyle',
    'rowLabelClassName',
    'rowLabelStyle',
    'rowStyle',
    'rows',
    'style',
    'tableClassName',
    'tableStyle',
    'type',
    'variant',
  ],
  text: ['as', 'className', 'style', 'text', 'type', 'variant'],
};

/** Props on `table` rows, which are entries rather than nodes. */
export const KNOWN_TABLE_ROW_PROPS: readonly string[] = [
  'cellClassName',
  'cellStyle',
  'cells',
  'label',
  'rowClassName',
  'rowLabelClassName',
  'rowLabelStyle',
  'rowStyle',
  'when',
  'whenAll',
];
